import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';

const SOCKET_URL = process.env.REACT_APP_API_URL || '';

// ── Icons ─────────────────────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
function SmsIcon({ className })     { return <Ico className={className}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Ico>; }
function SendIcon({ className })    { return <Ico className={className}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></Ico>; }
function PlusIcon({ className })    { return <Ico className={className}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ico>; }
function BackIcon({ className })    { return <Ico className={className}><polyline points="15 18 9 12 15 6"/></Ico>; }
function XIcon({ className })       { return <Ico className={className}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>; }

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTime(date) {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)  return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatFull(date) {
  if (!date) return '';
  return new Date(date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function initials(number) {
  if (!number) return '?';
  const digits = number.replace(/\D/g, '');
  return digits.slice(-2);
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Messaging({ theme = 'dark', onUnreadCount }) {
  const { user } = useContext(AuthContext);

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv]       = useState(null);
  const [messages, setMessages]           = useState([]);
  const [loadingConvs, setLoadingConvs]   = useState(true);
  const [loadingMsgs, setLoadingMsgs]     = useState(false);
  const [draft, setDraft]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [numbers, setNumbers]             = useState([]);
  const [fromNumber, setFromNumber]       = useState('');
  const [newConvOpen, setNewConvOpen]     = useState(false);
  const [newTo, setNewTo]                 = useState('');
  const [error, setError]                 = useState('');
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [expanded, setExpanded]             = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const cardRef        = useRef(null);

  // Collapse right-panel expansion when clicking outside the card
  useEffect(() => {
    function handleClickOutside(e) {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDark = theme === 'dark';

  // ── Theme tokens ─────────────────────────────────────────────────────────
  const bg         = isDark ? 'bg-white/5'         : 'bg-slate-50';
  const bgActive   = isDark ? 'bg-sky-500/15'      : 'bg-blue-50';
  const border     = isDark ? 'border-white/10'    : 'border-slate-200';
  const text       = isDark ? 'text-white'         : 'text-slate-900';
  const textMuted  = isDark ? 'text-white/50'      : 'text-slate-500';
  const textFaint  = isDark ? 'text-white/30'      : 'text-slate-400';
  const input      = isDark
    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-sky-500 focus:ring-sky-500'
    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-blue-100';
  const btnPrimary = isDark
    ? 'bg-sky-500 hover:bg-sky-400 text-white'
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  const btnGhost   = isDark
    ? 'bg-white/8 hover:bg-white/15 text-white/70 border border-white/10'
    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200';
  const bubble_out = isDark ? 'bg-sky-500 text-white' : 'bg-blue-600 text-white';
  const bubble_in  = isDark ? 'bg-white/10 text-white' : 'bg-white border border-slate-200 text-slate-900';

  // ── Socket for real-time ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const sock = io(`${SOCKET_URL}/video`, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
    });
    sock.on('connect', () => {
      sock.emit('register', { userId: user.id, email: user.email, tenantId: user.tenantId, type: 'messaging' });
    });
    sock.on('message-new', (payload) => {
      // Update conversation list
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === payload.conversationId);
        if (idx === -1) {
          fetchConversations();
          return prev;
        }
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          lastMessageBody: payload.body,
          lastMessageAt: payload.createdAt,
          unreadCount: payload.unreadCount,
        };
        return updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
      });
      // If this conversation is open, append the message
      setActiveConv(conv => {
        if (conv?.id === payload.conversationId) {
          setMessages(msgs => [...msgs, {
            id: Date.now().toString(),
            direction: 'inbound',
            body: payload.body,
            channel: payload.channel,
            createdAt: payload.createdAt,
            fromNumber: payload.contactNumber,
          }]);
          // Mark read immediately
          api.post(`/messaging/conversations/${payload.conversationId}/read`).catch(() => {});
          setConversations(prev => prev.map(c =>
            c.id === payload.conversationId ? { ...c, unreadCount: 0 } : c
          ));
        }
        return conv;
      });
      // Update parent unread count
      setConversations(prev => {
        const total = prev.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        onUnreadCount?.(total);
        return prev;
      });
    });
    return () => sock.disconnect();
  }, [user?.id]);

  // ── Load conversations + numbers ──────────────────────────────────────────
  useEffect(() => { fetchConversations(); fetchNumbers(); }, []);

  const fetchConversations = async () => {
    try {
      setLoadingConvs(true);
      const res = await api.get('/messaging/conversations');
      const data = res.data.data || [];
      setConversations(data);
      const total = data.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      onUnreadCount?.(total);
    } catch (e) {
      setError('Failed to load conversations');
    } finally {
      setLoadingConvs(false);
    }
  };

  const fetchNumbers = async () => {
    try {
      const res = await api.get('/numbers');
      const nums = res.data?.data || res.data || [];
      setNumbers(nums);
      if (nums.length > 0 && !fromNumber) setFromNumber(nums[0].phoneNumber || nums[0].phone_number || nums[0].number || '');
    } catch (_) {}
  };

  // ── Open conversation ─────────────────────────────────────────────────────
  const openConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    setExpanded(true);
    // Always reply from the same number the conversation is on
    if (conv.ourNumber) setFromNumber(conv.ourNumber);
    setMobileShowChat(true);
    setLoadingMsgs(true);
    setMessages([]);
    try {
      const res = await api.get(`/messaging/conversations/${conv.id}/messages`);
      setMessages(res.data.data || []);
      // Mark read
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unreadCount: 0 } : c));
      setConversations(prev => {
        const total = prev.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        onUnreadCount?.(total);
        return prev;
      });
    } catch (_) {
      setError('Failed to load messages');
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when conversation opens
  useEffect(() => {
    if (activeConv) setTimeout(() => inputRef.current?.focus(), 100);
  }, [activeConv]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!draft.trim() || !activeConv || sending) return;
    const body = draft.trim();
    setDraft('');
    setSending(true);

    // Optimistic message
    const tempMsg = {
      id: 'temp-' + Date.now(),
      direction: 'outbound',
      body,
      channel: activeConv.channel,
      createdAt: new Date().toISOString(),
      status: 'sending',
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      await api.post('/messaging/send', {
        to: activeConv.contactNumber,
        from: fromNumber || activeConv.ourNumber,
        message: body,
        channel: activeConv.channel,
      });
      // Replace temp with confirmed
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? { ...m, status: 'sent' } : m));
      setConversations(prev => prev.map(c =>
        c.id === activeConv.id ? { ...c, lastMessageBody: body, lastMessageAt: new Date().toISOString() } : c
      ));
    } catch (_) {
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? { ...m, status: 'failed' } : m));
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // ── Start new conversation ────────────────────────────────────────────────
  const handleNewConv = async () => {
    if (!newTo.trim() || !fromNumber) return;
    // Normalize number
    const to = newTo.trim().startsWith('+') ? newTo.trim() : '+1' + newTo.trim().replace(/\D/g, '');
    // Check if conversation already exists
    const existing = conversations.find(c => c.contactNumber === to && c.ourNumber === fromNumber);
    if (existing) {
      openConversation(existing);
      setNewConvOpen(false);
      setNewTo('');
      return;
    }
    // Create by sending first message (or just open a virtual new thread)
    const pseudo = {
      id: '__new__',
      tenantId: user.tenantId,
      contactNumber: to,
      ourNumber: fromNumber,
      channel: 'sms',
      lastMessageBody: null,
      lastMessageAt: null,
      unreadCount: 0,
    };
    setActiveConv(pseudo);
    setMessages([]);
    setMobileShowChat(true);
    setNewConvOpen(false);
    setNewTo('');
  };

  const handleSendNew = async () => {
    if (!draft.trim() || !activeConv || sending) return;
    if (activeConv.id !== '__new__') { handleSend(); return; }

    const body = draft.trim();
    setDraft('');
    setSending(true);
    try {
      await api.post('/messaging/send', {
        to: activeConv.contactNumber,
        from: fromNumber,
        message: body,
        channel: 'sms',
      });
      await fetchConversations();
      // Find the newly created conversation
      const res = await api.get('/messaging/conversations');
      const convs = res.data.data || [];
      const newConv = convs.find(c => c.contactNumber === activeConv.contactNumber && c.ourNumber === fromNumber);
      if (newConv) {
        setActiveConv(newConv);
        const msgRes = await api.get(`/messaging/conversations/${newConv.id}/messages`);
        setMessages(msgRes.data.data || []);
      }
    } catch (_) {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const actualSend = activeConv?.id === '__new__' ? handleSendNew : handleSend;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={cardRef} className={`flex h-full overflow-hidden rounded-[24px] border ${border} ${text}`}>

      {/* ── Left: conversation list ─────────────────────────────────────── */}
      {/* Inline width overrides Tailwind when expanded; Tailwind takes over when not */}
      <div
        className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r ${border} flex-shrink-0 overflow-hidden`}
        style={{
          width: expanded ? '72px' : undefined,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >

        {/* Header */}
        <div className={`flex items-center border-b ${border} flex-shrink-0 ${expanded ? 'justify-center px-2 py-3' : 'justify-between px-4 py-3'}`}>
          {!expanded && <h2 className="font-semibold text-base">Messages</h2>}
          <motion.button
            whileTap={{ scale: 0.93 }}
            onClick={() => { setExpanded(false); setNewConvOpen(true); }}
            className={`w-8 h-8 flex items-center justify-center rounded-xl transition ${btnPrimary}`}
            title="New message"
          >
            <PlusIcon className="h-4 w-4" />
          </motion.button>
        </div>

        {/* New conversation form */}
        <AnimatePresence>
          {newConvOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`px-4 py-3 border-b ${border} overflow-hidden`}
            >
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={newTo}
                  onChange={e => setNewTo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleNewConv()}
                  placeholder="Phone number…"
                  className={`flex-1 px-3 py-1.5 rounded-xl border text-sm outline-none focus:ring-2 transition ${input}`}
                />
                <motion.button whileTap={{ scale: 0.93 }} onClick={handleNewConv}
                  className={`px-3 py-1.5 rounded-xl text-sm font-medium transition ${btnPrimary}`}>
                  Go
                </motion.button>
                <motion.button whileTap={{ scale: 0.93 }} onClick={() => { setNewConvOpen(false); setNewTo(''); }}
                  className={`w-8 h-8 flex items-center justify-center rounded-xl transition ${btnGhost}`}>
                  <XIcon className="h-3.5 w-3.5" />
                </motion.button>
              </div>
              {numbers.length > 0 && (
                <div className="mt-2">
                  <select
                    value={fromNumber}
                    onChange={e => setFromNumber(e.target.value)}
                    className={`w-full px-3 py-1.5 rounded-xl border text-xs outline-none focus:ring-2 transition ${input}`}
                  >
                    {numbers.map(n => {
                      const num = n.phoneNumber || n.phone_number || n.number || '';
                      return <option key={num} value={num}>{num}</option>;
                    })}
                  </select>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loadingConvs && (
            <div className="space-y-2 p-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`h-16 rounded-2xl animate-pulse ${bg}`} />
              ))}
            </div>
          )}

          {!loadingConvs && conversations.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${bg}`}>
                <SmsIcon className={`h-5 w-5 ${textMuted}`} />
              </div>
              <p className={`text-sm ${textMuted}`}>No conversations yet</p>
              <motion.button whileTap={{ scale: 0.95 }} onClick={() => setNewConvOpen(true)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${btnPrimary}`}>
                New Message
              </motion.button>
            </div>
          )}

          <AnimatePresence>
            {conversations.map((conv, i) => {
              const isActive = activeConv?.id === conv.id;
              return (
                <motion.button
                  key={conv.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.2) }}
                  onClick={() => openConversation(conv)}
                  title={expanded ? conv.contactNumber : undefined}
                  className={`w-full flex items-center border-b text-left transition ${border} ${
                    isActive ? bgActive : 'hover:bg-white/5'
                  } ${expanded ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'}`}
                >
                  {/* Avatar — always visible; unread badge in slim mode */}
                  <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    isActive
                      ? isDark ? 'bg-sky-500 text-white' : 'bg-blue-600 text-white'
                      : isDark ? 'bg-white/10 text-white/70' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {initials(conv.contactNumber)}
                    {expanded && conv.unreadCount > 0 && (
                      <span className={`absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                        isDark ? 'bg-sky-500 text-white' : 'bg-blue-600 text-white'
                      }`}>
                        {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                      </span>
                    )}
                  </div>

                  {/* Info — only in full mode */}
                  {!expanded && (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className={`text-sm font-semibold truncate ${text}`}>{conv.contactNumber}</span>
                        <span className={`text-xs flex-shrink-0 ${textFaint}`}>{formatTime(conv.lastMessageAt)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-1 mt-0.5">
                        <span className={`text-xs truncate ${textMuted}`}>
                          {conv.lastMessageBody || 'No messages yet'}
                        </span>
                        {conv.unreadCount > 0 && (
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            isDark ? 'bg-sky-500 text-white' : 'bg-blue-600 text-white'
                          }`}>
                            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Right: chat window ──────────────────────────────────────────── */}
      <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0`}>

        {!activeConv ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${bg}`}>
              <SmsIcon className={`h-7 w-7 ${textMuted}`} />
            </div>
            <p className={`text-sm ${textMuted}`}>Select a conversation or start a new one</p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className={`flex items-center gap-3 px-4 py-3 border-b ${border} flex-shrink-0`}>
              <motion.button
                whileTap={{ scale: 0.93 }}
                onClick={() => { setMobileShowChat(false); setActiveConv(null); }}
                className={`md:hidden w-8 h-8 flex items-center justify-center rounded-xl transition ${btnGhost}`}
              >
                <BackIcon className="h-4 w-4" />
              </motion.button>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                isDark ? 'bg-sky-500/20 text-sky-400' : 'bg-blue-100 text-blue-700'
              }`}>
                {initials(activeConv.contactNumber)}
              </div>
              <div>
                <p className={`text-sm font-semibold ${text}`}>{activeConv.contactNumber}</p>
                <p className={`text-xs ${textFaint}`}>
                  {activeConv.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} · via {activeConv.ourNumber || fromNumber}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
              {loadingMsgs && (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className={`h-10 w-48 rounded-2xl animate-pulse ${bg} ${i % 2 === 0 ? 'ml-auto' : ''}`} />
                  ))}
                </div>
              )}

              {!loadingMsgs && messages.length === 0 && (
                <div className="flex items-center justify-center h-32">
                  <p className={`text-sm ${textMuted}`}>No messages yet — say hello!</p>
                </div>
              )}

              {messages.map((msg) => {
                const isOut = msg.direction === 'outbound';
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] group`}>
                      <div className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                        isOut ? bubble_out : bubble_in
                      } ${isOut ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
                        {msg.body}
                      </div>
                      <p className={`text-xs mt-1 ${textFaint} ${isOut ? 'text-right' : 'text-left'} opacity-0 group-hover:opacity-100 transition`}>
                        {formatFull(msg.createdAt)}
                        {isOut && msg.status === 'failed' && <span className="ml-1 text-red-400">· Failed</span>}
                        {isOut && msg.status === 'sending' && <span className="ml-1">· Sending…</span>}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Compose bar */}
            <div className={`px-4 py-3 border-t ${border} flex-shrink-0`}>
              {numbers.length > 1 && (
                <div className="mb-2">
                  <select
                    value={fromNumber}
                    onChange={e => setFromNumber(e.target.value)}
                    className={`w-full px-3 py-1.5 rounded-xl border text-xs outline-none focus:ring-2 transition ${input}`}
                  >
                    {numbers.map(n => {
                      const num = n.phoneNumber || n.phone_number || n.number || '';
                      return <option key={num} value={num}>{num}</option>;
                    })}
                  </select>
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); actualSend(); }
                  }}
                  placeholder="Type a message…"
                  rows={1}
                  className={`flex-1 px-3.5 py-2.5 rounded-2xl border text-sm outline-none focus:ring-2 transition resize-none ${input}`}
                  style={{ maxHeight: '120px', overflowY: 'auto' }}
                  onInput={e => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                />
                <motion.button
                  whileTap={{ scale: 0.93 }}
                  onClick={actualSend}
                  disabled={!draft.trim() || sending}
                  className={`w-10 h-10 flex items-center justify-center rounded-2xl transition flex-shrink-0 disabled:opacity-40 ${btnPrimary}`}
                >
                  <SendIcon className="h-4 w-4" />
                </motion.button>
              </div>
              <p className={`text-xs mt-1.5 ${textFaint}`}>Enter to send · Shift+Enter for new line</p>
            </div>
          </>
        )}
      </div>

      {/* Error toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl text-sm bg-red-500 text-white shadow-lg z-50"
            onClick={() => setError('')}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
